import { API_BASE, ApiError, api } from "@/lib/apiClient";

export type EmployeeTimeEntryType = "manual" | "timer" | "range" | "break" | "hero";

export interface EmployeeTimeEntry {
  id: number;
  auftrag_id: number | null;
  user_login: string;
  user_display: string | null;
  work_date: string;
  hours_decimal: number;
  time_from: string | null;
  time_to: string | null;
  entry_type: EmployeeTimeEntryType;
  comment: string | null;
  is_homeoffice?: boolean;
  created_by_login: string | null;
  created_at: string;
  updated_at: string;
  auftragsnummer?: string | null;
  jahr?: number | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  beschreibung?: string | null;
  geschaeftsdokument_position_id?: number | null;
  position_bezeichnung?: string | null;
  position_nr?: number | null;
  position_preisart?: "festpreis" | "nachweis" | null;
  workflow_status?: string | null;
  project_completed?: boolean | null;
  late_hour_permission?: boolean | null;
  timesheet_id?: number | null;
  claimed_hours_decimal?: number | null;
  timesheet_display_number?: string | null;
  timesheet_status?: string | null;
  subcontractor_paid_at?: string | null;
  subcontractor_paid_by_login?: string | null;
  subcontractor_invoice_number?: string | null;
}

export interface EmployeeTimeAccount {
  user_login: string;
  user_display: string | null;
  total_hours: number;
  month_hours: number;
  entry_count: number;
  project_count: number;
}

export interface EmployeeTimeMonthDay {
  work_date: string;
  total_hours: number;
  entry_count: number;
  pending_justification?: boolean;
  counts_in_totals?: boolean;
}

export interface EmployeeTimeMonthProject {
  auftrag_id: number;
  auftragsnummer: string | null;
  jahr?: number | null;
  kunde: string | null;
  beschreibung: string | null;
  total_hours: number;
  entry_count: number;
  app_hours?: number;
  hero_hours?: number;
  app_entry_count?: number;
  hero_entry_count?: number;
}

export interface EmployeeTimeMonthSummary {
  user_login: string;
  user_display: string | null;
  year: number;
  month: number;
  total_hours: number;
  expected_hours?: number;
  app_expected_hours?: number | null;
  hero_expected_hours?: number | null;
  days: EmployeeTimeMonthDay[];
  by_project: EmployeeTimeMonthProject[];
  opening_balance_hours?: number | null;
  opening_effective_date?: string | null;
  overtime_balance_hours?: number | null;
  /** App-Soll je ISO-Tag. */
  expected_hours_by_day?: Record<string, number> | null;
  /** Laufender Überstundenstand je ISO-Tag; null wenn kein Startstand. */
  overtime_balance_by_day?: Record<string, number> | null;
  /** False für Nachunternehmer: nur Ist, kein Soll/Überstundenkonto. */
  has_soll?: boolean;
  tracks_overtime?: boolean;
}

export interface EmployeeTimeYearDayCell {
  day: number;
  total_hours: number;
  pending_justification?: boolean;
  counts_in_totals?: boolean;
}

export interface EmployeeTimeYearMonth {
  month: number;
  total_hours: number;
  days: EmployeeTimeYearDayCell[];
}

export interface EmployeeTimeYearSummary {
  user_login: string;
  user_display: string | null;
  year: number;
  total_hours: number;
  expected_hours?: number;
  app_expected_hours?: number | null;
  hero_expected_hours?: number | null;
  months: EmployeeTimeYearMonth[];
  opening_balance_hours?: number | null;
  opening_effective_date?: string | null;
  overtime_balance_hours?: number | null;
  /** Laufender Überstundenstand (Aktuell +/-) je ISO-Tag. */
  overtime_balance_by_day?: Record<string, number> | null;
  /** App-Soll je ISO-Tag (Arbeitszeitentabelle). */
  expected_hours_by_day?: Record<string, number> | null;
  /** Ausbezahlte Überstunden je ISO-Tag. */
  overtime_payouts_by_day?: Record<string, number> | null;
  /** False für Nachunternehmer: nur Ist, kein Soll/Überstundenkonto. */
  has_soll?: boolean;
  tracks_overtime?: boolean;
}

export interface EmployeeTimeTimer {
  id: number;
  user_login: string;
  auftrag_id: number;
  started_at: string;
  comment: string | null;
  is_homeoffice?: boolean;
  elapsed_seconds: number | null;
  auftragsnummer?: string | null;
  jahr?: number | null;
  kunde?: string | null;
  ort?: string | null;
  ort_zusatz?: string | null;
  beschreibung?: string | null;
  geschaeftsdokument_position_id?: number | null;
  position_bezeichnung?: string | null;
  position_nr?: number | null;
}

export interface ListEmployeeTimesParams {
  auftrag_id?: number;
  user_login?: string;
  work_date_from?: string;
  work_date_to?: string;
  year?: number;
  month?: number;
}

export interface EmployeeTimeEmployeeProfile {
  user_login: string;
  is_monteur: boolean;
  project_required: boolean;
  /** Homeoffice-Markierung nur für Büro (Nicht-Monteure). */
  homeoffice_allowed?: boolean;
}

export interface DayEntryBatchRow {
  id?: number;
  auftrag_id?: number | null;
  time_from?: string;
  time_to?: string;
  entry_type?: EmployeeTimeEntryType;
  comment?: string;
  geschaeftsdokument_position_id?: number | null;
  is_homeoffice?: boolean;
  delete?: boolean;
}

export interface CreateEmployeeTimePayload {
  auftrag_id?: number | null;
  work_date: string;
  hours_decimal: number;
  user_login?: string;
  user_display?: string;
  time_from?: string;
  time_to?: string;
  entry_type?: EmployeeTimeEntryType;
  comment?: string;
  geschaeftsdokument_position_id?: number | null;
  is_homeoffice?: boolean;
}

export interface UpdateEmployeeTimePayload {
  auftrag_id?: number;
  work_date?: string;
  hours_decimal?: number;
  user_login?: string;
  user_display?: string;
  time_from?: string;
  time_to?: string;
  entry_type?: EmployeeTimeEntryType;
  comment?: string;
  geschaeftsdokument_position_id?: number | null;
  is_homeoffice?: boolean;
}

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export function listEmployeeTimes(params: ListEmployeeTimesParams = {}): Promise<EmployeeTimeEntry[]> {
  return api.get(`/employee-times${buildQuery(params as Record<string, string | number | boolean | undefined | null>)}`);
}

export function getEmployeeTimeProfile(userLogin: string): Promise<EmployeeTimeEmployeeProfile> {
  return api.get(
    `/employee-times/employee-profile${buildQuery({ user_login: userLogin })}`,
  );
}

export function saveEmployeeDayBatch(payload: {
  user_login: string;
  user_display?: string;
  work_date: string;
  rows: DayEntryBatchRow[];
}): Promise<{
  status: string;
  saved_ids: number[];
  deleted_ids: number[];
  entries: EmployeeTimeEntry[];
  compliance?: import("@/lib/employeeTimeCompliance").EmployeeTimeDayCompliance;
}> {
  return api.post("/employee-times/day-batch", payload);
}

export function createEmployeeTime(payload: CreateEmployeeTimePayload): Promise<EmployeeTimeEntry> {
  return api.post("/employee-times", payload);
}

export function updateEmployeeTime(
  id: number,
  payload: UpdateEmployeeTimePayload,
): Promise<EmployeeTimeEntry> {
  return api.put(`/employee-times/${id}`, payload);
}

export function deleteEmployeeTime(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/employee-times/${id}`);
}

export function listEmployeeTimeAccounts(params?: {
  year?: number;
  month?: number;
}): Promise<EmployeeTimeAccount[]> {
  return api.get(`/employee-times/accounts${buildQuery(params ?? {})}`);
}

export function getEmployeeMonthSummary(params: {
  user_login: string;
  year: number;
  month: number;
  /** Nur Tagesstunden – ohne Hero/Projekte/Soll (Erfassungs-Kalender). */
  lite?: boolean;
  include_hero?: boolean;
}): Promise<EmployeeTimeMonthSummary> {
  return api.get(`/employee-times/month-summary${buildQuery(params)}`);
}

export function getEmployeeYearSummary(params: {
  user_login: string;
  year: number;
}): Promise<EmployeeTimeYearSummary> {
  return api.get(`/employee-times/year-summary${buildQuery(params)}`);
}

export interface EmployeeAccountSummary {
  user_login: string;
  year: number;
  overtime_balance_hours: number | null;
  hero_overtime_adjustment_hours?: number | null;
  opening_balance_hours?: number | null;
  opening_effective_date?: string | null;
  overtime_source?: "app" | "hero" | null;
  /** ISO-Datum des Stands (Vortag), wenn App-Berechnung heutigen Tag ausklammert. */
  overtime_as_of?: string | null;
  overtime_excludes_today?: boolean;
  /** Wochenstunden ÷ Arbeitstage des zugewiesenen Modells. */
  average_daily_soll_hours?: number | null;
  /** Arbeitstage laut aktuell gültigem Arbeitszeitmodell (ISO 1=Mo … 7=So). */
  work_weekdays?: number[] | null;
  vacation_days?: number | null;
  vacation_carryover_days?: number | null;
  total_vacation_budget?: number | null;
  used_vacation_days?: number | null;
  pending_vacation_days?: number | null;
  remaining_vacation_days: number | null;
  available_vacation_days?: number | null;
  vacation_source?: "app" | "hero" | null;
  hero_linked?: boolean;
  has_soll?: boolean;
  tracks_overtime?: boolean;
}

export function getEmployeeAccountSummary(params: {
  user_login: string;
  year: number;
  /** false überspringt den Hero-Saldo. Urlaub und Überstunden aus der App bleiben vollständig. */
  include_hero_balance?: boolean;
}): Promise<EmployeeAccountSummary> {
  return api.get(
    `/employee-times/account-summary${buildQuery({
      user_login: params.user_login,
      year: params.year,
      include_hero_balance: params.include_hero_balance === false ? false : undefined,
    })}`,
  );
}

export async function downloadEmployeeTimesExport(params: {
  user_login: string;
  year: number;
  month?: number;
  include_hero?: boolean;
}): Promise<void> {
  const query = buildQuery({
    user_login: params.user_login,
    year: params.year,
    month: params.month,
    include_hero: params.include_hero ? true : undefined,
  });
  const base = API_BASE || "";
  const url = `${base}/employee-times/export.xlsx${query}`;
  const res = await fetch(url, { credentials: "include" });
  if (res.status === 401) {
    throw new ApiError(401, "Nicht angemeldet");
  }
  if (!res.ok) {
    let message = `Export fehlgeschlagen (${res.status})`;
    try {
      const body = await res.json();
      if (body && typeof body.detail === "string") message = body.detail;
    } catch {
      // ignore
    }
    throw new ApiError(res.status, message);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename=\"?([^\";]+)\"?/i);
  const filename = match?.[1] || `Arbeitszeiten_${params.user_login}.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

export interface EmployeeOvertimeOpeningBalance {
  user_login: string;
  user_display: string | null;
  effective_date: string;
  hours_decimal: number;
  current_balance_hours: number | null;
  note: string | null;
  updated_at: string | null;
  updated_by_login: string | null;
}

export function listOvertimeOpeningBalances(params?: {
  user_login?: string;
}): Promise<EmployeeOvertimeOpeningBalance[]> {
  return api.get(`/employee-times/overtime-opening-balances${buildQuery(params ?? {})}`, {
    timeoutMs: params?.user_login ? 30_000 : 60_000,
  });
}

export function upsertOvertimeOpeningBalance(payload: {
  user_login: string;
  user_display?: string;
  effective_date: string;
  hours_decimal: number;
  note?: string;
}): Promise<EmployeeOvertimeOpeningBalance> {
  return api.put("/employee-times/overtime-opening-balances", payload);
}

export interface EmployeeOvertimePayout {
  id: number;
  user_login: string;
  user_display: string | null;
  payment_date: string;
  hours_decimal: number;
  note: string | null;
  created_at: string | null;
  created_by_login: string | null;
  updated_at: string | null;
  updated_by_login: string | null;
}

export function listOvertimePayouts(params?: {
  user_login?: string;
  year?: number;
  date_from?: string;
  date_to?: string;
}): Promise<EmployeeOvertimePayout[]> {
  return api.get(`/employee-times/overtime-payouts${buildQuery(params ?? {})}`);
}

export function createOvertimePayout(payload: {
  user_login: string;
  user_display?: string;
  payment_date: string;
  hours_decimal: number;
  note?: string;
}): Promise<EmployeeOvertimePayout> {
  return api.post("/employee-times/overtime-payouts", payload);
}

export function updateOvertimePayout(
  payoutId: number,
  payload: {
    payment_date?: string;
    hours_decimal?: number;
    note?: string;
    user_display?: string;
  },
): Promise<EmployeeOvertimePayout> {
  return api.put(`/employee-times/overtime-payouts/${payoutId}`, payload);
}

export function deleteOvertimePayout(payoutId: number): Promise<{ status: string; id: number }> {
  return api.del(`/employee-times/overtime-payouts/${payoutId}`);
}

export function getActiveTimer(userLogin?: string): Promise<EmployeeTimeTimer | null> {
  return api.get(`/employee-times/timer${buildQuery({ user_login: userLogin })}`);
}

export function startEmployeeTimer(payload: {
  auftrag_id: number;
  user_login?: string;
  comment?: string;
  geschaeftsdokument_position_id?: number | null;
  is_homeoffice?: boolean;
}): Promise<EmployeeTimeTimer> {
  return api.post("/employee-times/timer/start", payload);
}

export function stopEmployeeTimer(userLogin?: string): Promise<EmployeeTimeEntry> {
  return api.post(`/employee-times/timer/stop${buildQuery({ user_login: userLogin })}`);
}

export function getEmployeeDayCompliance(params: {
  user_login: string;
  work_date: string;
}): Promise<import("@/lib/employeeTimeCompliance").EmployeeTimeDayCompliance> {
  return api.get(`/employee-times/day-compliance${buildQuery(params)}`);
}

export interface EmployeeTimeDayApprovalProject {
  auftrag_id: number | null;
  auftragsnummer: string | null;
  jahr: number | null;
  kunde: string | null;
  ort: string | null;
  beschreibung: string | null;
  hours: number;
}

export interface EmployeeTimeDayApproval {
  id: number;
  user_login: string;
  user_display: string | null;
  work_date: string;
  status: "pending" | "approved" | "rejected" | "justified";
  violations: import("@/lib/employeeTimeCompliance").TimeComplianceViolation[];
  work_hours: number | null;
  break_minutes: number | null;
  requested_at: string;
  requested_by_login: string | null;
  reviewed_by_login: string | null;
  reviewed_by_display: string | null;
  reviewed_at: string | null;
  review_comment: string | null;
  employee_justification?: string | null;
  justification_created_at?: string | null;
  justification_updated_at?: string | null;
  justification_by_login?: string | null;
  justification_by_display?: string | null;
  has_justification?: boolean;
  requires_justification?: boolean;
  justification_locked?: boolean;
  historical_gf_review?: boolean;
  projects?: EmployeeTimeDayApprovalProject[];
}

/** @deprecated Liefert immer [] – GF-Freigabe für Tagesverstöße entfällt. */
export function listPendingDayApprovals(): Promise<EmployeeTimeDayApproval[]> {
  return api.get("/employee-times/pending-approvals");
}

export function listDayViolations(params?: {
  status?: "all" | "pending" | "justified" | "historical";
  user_login?: string;
  date_from?: string;
  date_to?: string;
  violation_code?: string;
}): Promise<EmployeeTimeDayApproval[]> {
  return api.get(`/employee-times/day-violations${buildQuery(params ?? {})}`);
}

export function saveDayJustification(payload: {
  user_login: string;
  work_date: string;
  justification: string;
}): Promise<{
  status: string;
  message: string;
  compliance: import("@/lib/employeeTimeCompliance").EmployeeTimeDayCompliance;
}> {
  return api.post("/employee-times/day-justification", payload);
}

/** @deprecated GF-Freigabe ist deaktiviert – Endpoint liefert 403. */
export function approveEmployeeDay(
  approvalId: number,
  comment?: string,
): Promise<EmployeeTimeDayApproval> {
  return api.post(`/employee-times/day-approvals/${approvalId}/approve`, { comment });
}

/** @deprecated GF-Freigabe ist deaktiviert – Endpoint liefert 403. */
export function rejectEmployeeDay(
  approvalId: number,
  comment?: string,
): Promise<EmployeeTimeDayApproval> {
  return api.post(`/employee-times/day-approvals/${approvalId}/reject`, { comment });
}

export function formatHours(value: number | string | null | undefined): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0,00 h";
  return `${num.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`;
}

export interface EmployeeDailyHoursCell {
  user_login: string;
  work_date: string;
  hours: number;
  paid_hours?: number;
}

export function listSubcontractorEntries(params?: {
  year?: number;
  month?: number;
  /** Arbeitstag ab, ISO-Datum (inklusive). */
  date_from?: string;
  /** Arbeitstag bis, ISO-Datum (inklusive). */
  date_to?: string;
  user_login?: string;
  paid?: "open" | "paid" | "all";
}): Promise<EmployeeTimeEntry[]> {
  return api.get(`/employee-times/subcontractor-entries${buildQuery(params ?? {})}`);
}

export function markSubcontractorEntriesPaid(payload: {
  entry_ids: number[];
  invoice_number: string;
}): Promise<{ status: string; updated: number; invoice_number: string }> {
  return api.post("/employee-times/subcontractor-entries/mark-paid", payload);
}

export function unmarkSubcontractorEntriesPaid(payload: {
  entry_ids: number[];
}): Promise<{ status: string; updated: number }> {
  return api.post("/employee-times/subcontractor-entries/unmark-paid", payload);
}

export interface BookedProjectDay {
  user_login: string;
  work_date: string;
  auftrag_id: number;
  auftragsnummer: string | null;
  kunde: string | null;
  ort: string | null;
  hours: number;
  workflow_status?: string | null;
  workflow_status_secondary?: string | null;
  beschreibung?: string | null;
}

export async function fetchBookedProjectsBatch(params: {
  date_from: string;
  date_to: string;
  user_logins: string[];
}): Promise<{ items: BookedProjectDay[] }> {
  const logins = params.user_logins.map((l) => l.trim()).filter(Boolean).join(",");
  return api.get(
    `/employee-times/booked-projects-batch${buildQuery({
      date_from: params.date_from,
      date_to: params.date_to,
      user_logins: logins,
    })}`,
  );
}

export async function fetchEmployeeDailyHoursBatch(params: {
  date_from: string;
  date_to: string;
  user_logins: string[];
}): Promise<{ hours: EmployeeDailyHoursCell[] }> {
  const logins = params.user_logins.map((l) => l.trim()).filter(Boolean).join(",");
  return api.get(
    `/employee-times/daily-hours-batch${buildQuery({
      date_from: params.date_from,
      date_to: params.date_to,
      user_logins: logins,
    })}`,
  );
}

export async function fetchMontageplanOvertimeBalances(params: {
  user_logins: string[];
}): Promise<{ overtime_balance_hours: Record<string, number> }> {
  const logins = params.user_logins.map((l) => l.trim()).filter(Boolean).join(",");
  return api.get(
    `/employee-times/montageplan-overtime-balances${buildQuery({
      user_logins: logins,
    })}`,
  );
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Berechnet Stunden aus HH:MM–HH:MM (über Mitternacht wird berücksichtigt). */
export function computeHoursFromTimeRange(timeFrom: string, timeTo: string): number {
  const parse = (value: string) => {
    const parts = value.split(":").map(Number);
    const h = parts[0];
    const m = parts[1];
    if (h === undefined || m === undefined || !Number.isFinite(h) || !Number.isFinite(m)) {
      return null;
    }
    return h * 60 + m;
  };
  const from = parse(timeFrom);
  const to = parse(timeTo);
  if (from === null || to === null) return 0;
  let diff = to - from;
  if (diff <= 0) diff += 24 * 60;
  return Math.round((diff / 60) * 100) / 100;
}

export function nowTimeInputValue(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
